<template>
  <div class="files-table">
    <FilterBar
      cookieName="FilesFilterOptions"
      filterBarType="files"
      @change="(filters) => setupData(true, filters)"
      :mainFilters="mainFilters"
      :secondaryFilters="secondaryFilters"
      ref="files-table-filterbar"
    />

    <div class="content--inner">
      <overlay-scrollbars :options="scrollBarsOptions" class="files-table-scrollbar">
        <b-table
          primary-key="id"
          :class="['gk-table gk-table--responsive gk-table--striped']"
          :items="temporaryFiles"
          :fields="fields"
          :tbody-tr-class="getRowClasses"
          @row-clicked="(row) => showModal(row.uuid)"
          :key="tableKey"
          :busy="loading"
          show-empty
          table-busy
        >
          <template #empty="">
            <EmptyState>
              <template v-slot:image>
                <img src="/static/img/empty-states/empty-state-cost.png" />
              </template>
              <template v-slot:title>
                {{ $gettext("We couldn't find any downloads") }}
              </template>
            </EmptyState>
          </template>
          <template #table-busy="">
            <LoadingSpinner min-height />
          </template>

          <template #cell(file)="data">
            <div class="files-row-name">
              <font-awesome-icon :icon="`fa ${getIconForFile(data.item.mime_type)}`" />
              <span :class="data.item.status">
                {{ getFileName2(data.item) }}
              </span>
            </div>
          </template>

          <template #cell(expiry)="data">
            <div
              v-if="counters[data.item.uuid] && getSecondsLeft(data.item) > 0 && !currentUser.is_accountant"
              style="display: flex; justify-content: center"
            >
              <CircularProgressBar :value="getTimeoutPercentage(data.item)" bg-color="#f3f4f5">
                <GkBadge
                  :id="`timeout-hint-${data.item.uuid}`"
                  icon="fa-solid fa-crown"
                  icon-only
                  type="premium"
                  variant="light"
                  :tooltip-text="
                    $gettext(
                      'This download is in the processing queue, upgrade to premium to skip the line and download any file immediately.'
                    )
                  "
                />
              </CircularProgressBar>
            </div>
            <div v-else style="display: flex; justify-content: flex-end">
              <div class="status-badge" :class="'status-' + data.item.status">
                <div
                  v-if="data.item.status === 'processing'"
                  class="status-circle-badge badge-spinner"
                  style="width: 13px; height: 13px"
                />
                {{ getStatusBadgeText(data.item.status) }}
              </div>
            </div>
          </template>

          <template #cell(created)="data">
            {{ data.value | formatDateWithTime }}
          </template>

          <template slot="bottom-row" v-if="nextUrl">
            <td :colspan="fields.length" class="table-load-more">
              <GekkoButton
                v-if="nextUrl && !fetchingNext"
                @click="handleScroll()"
                id="load_more_button"
                class="text-info"
                variant="link"
                :text="$gettext('Load more files')"
              />
              <LoadingSpinner v-if="fetchingNext" />
            </td>
          </template>
        </b-table>
      </overlay-scrollbars>
    </div>
  </div>
</template>

<script>
import TemporaryFileService from '@/store/services/temporaryfile.service'
import GekkoButton from '@/components/GekkoButton.vue'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterBar from '@/components/list_filters/FilterBar.vue'
import TotalAmountsCost from '@/components/costs/TotalAmountsCost.vue'
import TitleBar from '@/components/TitleBar.vue'
import { extensionFromMimeType, getFileName, getSecondsLeft } from '@/utils'
import { mapGetters } from 'vuex'
import GkBadge from '@/components/GkBadge.vue'
import CircularProgressBar from '@/components/ui/CircularProgressBar.vue'

export default {
  name: 'FilesTable',
  components: {
    CircularProgressBar,
    GkBadge,
    TitleBar,
    TotalAmountsCost,
    FilterBar,
    EmptyState,
    LoadingSpinner,
    GekkoButton,
  },
  props: {
    mainFilters: {
      type: Array,
      default: () => ['DateRangeFilter', 'FileSubtypeFilter'],
    },
    secondaryFilters: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters({
      userId: 'main/getUserId',
      currentUser: 'main/getCurrentUser',
      currentCompany: 'main/getCurrentCompany',
      getCompanyId: 'main/getCompanyId',
    }),
  },
  data() {
    return {
      temporaryFiles: [],
      loading: true,
      fetchingNext: false,
      scrollBarsOptions: {
        callbacks: {
          onScrollStop: this.handleScroll,
        },
      },
      tableKey: 0,
      nextUrl: '',
      fields: [
        {
          key: 'file',
          label: this.$gettext('File'),
          sortable: false,
          tdClass: 'col-file-filename',
        },
        {
          key: 'expiry',
          label: this.$gettext('Status'),
          sortable: false,
          tdClass: 'col-file-status',
        },
        {
          key: 'created',
          label: this.$gettext('Created'),
          sortable: true,
          tdClass: 'col-file-created',
        },
      ],
      counters: {},
      interval: null,
      pollingInterval: null,
      MAX_NUM_ATTEMPTS: 100,
      COOLDOWN_SECONDS: 5
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    clearInterval(this.interval)
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }
  },
  methods: {
    getFileName2: getFileName,
    extensionFromMimeType,
    getSecondsLeft,
    setupData(setLoading = true, filters) {
      if (setLoading) {
        this.loading = true
        this.$emit('loading', this.loading)
      }
      TemporaryFileService.list({ company_profile: this.getCompanyId, type: 'export', ...filters })
        .then((response) => {
          this.nextUrl = response.data.next
          this.temporaryFiles = response.data.results
          const now = Date.now()
          const processingFiles = this.temporaryFiles
            .filter((item) => item.status === 'processing')
            .map((item) => {
              const createdTimestamp = new Date(item.created).getTime()
              const secondsLeft = ((createdTimestamp + item.activate_after_seconds * 1000) - now) / 1000
              return [item.uuid, { ...item, seconds_left: secondsLeft }]
            })
          this.$set(this, 'counters', Object.fromEntries(processingFiles))
          this.interval = setInterval(() => {
            this.refreshProcessingFiles()
            if (Object.keys(this.counters).length === 0) {
              clearInterval(this.interval)
              this.interval = null
            }
          }, 1000)
        })
        .catch((_) => {})
        .finally(() => {
          this.loading = false
          this.$emit('loading', false)
        })

      window.addEventListener('scroll', this.handleScroll)
    },
    refreshProcessingFiles() {
      Object.keys(this.counters).forEach((counterKey) => {
        const counterItem = this.counters[counterKey]

        if (!counterItem) {
          return
        }

        if (counterItem.status !== 'processing') {
          this.$delete(this.counters, counterKey)
          return
        }
        
        if (counterItem.seconds_left > 0) {
          this.$set(this.counters[counterKey], 'seconds_left', counterItem.seconds_left - 1)
          return
        }

        TemporaryFileService.getById(counterItem.uuid).then((response) => {
          const attemptCount = counterItem.nAttempts ? counterItem.nAttempts : 0
          const nextAttemptCount = attemptCount + 1
          
          if (response.data.status === 'processing' && attemptCount < this.MAX_NUM_ATTEMPTS) {
            this.$set(this.counters, counterKey, {
              ...counterItem,
              ...response.data,
              nAttempts: nextAttemptCount,
              seconds_left: this.COOLDOWN_SECONDS
            })
          } else {
            this.$delete(this.counters, counterKey)
          }

          const tempFileIndex = this.temporaryFiles.findIndex((tempFile) => tempFile.uuid === counterKey)
          if (tempFileIndex !== -1) {
            this.$set(this.temporaryFiles, tempFileIndex, {
              ...this.temporaryFiles[tempFileIndex],
              ...response.data,
              nAttempts: response.data.status === 'processing' ? nextAttemptCount : attemptCount
            })
          }
        })
      })
      this.tableKey++
    },
    getFileNameWithExtension(url) {
      return /([\w-]+)(\.[\w-]+)+(?!.*\/)/.exec(url)
    },
    getFileName(file) {
      if (!file) return ''

      return this.getFileNameWithExtension(file)[1]
    },
    getIconForFile(mimeType) {
      switch (mimeType) {
        case 'application/zip':
          return 'fa-file-zip'

        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return 'fa-file-xls'

        case 'text/csv':
          return 'fa-file-csv'

        case 'application/xml':
          return 'fa-file-xml'

        case 'application/pdf':
          return 'fa-file-pdf'
      }

      return 'fa-file'
    },
    getRowClasses(item) {
      if (!item) return 'files-table-row'

      return item.status === 'active' ? 'files-table-row files-table-row-hover' : 'files-table-row'
    },
    showModal(id) {
      if (this.$route.name === 'ControlPanelFiles') {
        this.$router.push({ name: 'ControlPanelFileDetails', params: { id: id } })
      } else {
        this.$router.push({ name: 'FileDetails', params: { id: id } })
      }
    },
    regenerate(id) {
      if (!this.currentCompany.is_premium) {
        return this.$store.commit('togglePremiumModal', 'web_files_regenerate')
      }

      TemporaryFileService.regenerate(id)
        .then(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Download regenerated'),
            text: this.$gettext('Your download has been successfully regenerated, please check back in a moment'),
            variant: 'success',
            duration: 4,
          })
        })
        .then(() => {
          this.reloadFilterStore()
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Regeneration failed'),
            text: this.$gettext('Failed to regenerate your download. Please try again later.'),
            variant: 'danger',
            duration: 4,
          })
        })
    },
    reloadFilterStore() {
      this.$refs['files-table-filterbar'].reloadFilterStore()
      this.$refs['files-table-filterbar'].filtersChanged()
      this.tableKey++
    },
    getTimeoutPercentage(item) {
      const secondsLeft = this.counters[item.uuid].seconds_left
      return 100 - (secondsLeft / item.activate_after_seconds) * 100
    },
    getStatusBadgeText(type) {
      let badgeText = ''
      switch (type) {
        case 'processing':
          badgeText = this.$gettext('Processing')
          break
        case 'expired':
          badgeText = this.$gettext('Expired')
          break
        case 'active':
          badgeText = this.$gettext('Ready')
          break
        case 'failed':
          badgeText = this.$gettext('Failed')
          break
      }
      return badgeText
    },
    isInViewport(element_id) {
      const element = document.getElementById(element_id)
      if (!element) return false
      const rect = element.getBoundingClientRect()
      const html = document.documentElement
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || html.clientHeight) &&
        rect.right <= (window.innerWidth || html.clientWidth)
      )
    },
    handleScroll() {
      if (!this.loading && this.nextUrl) {
        const loadNewFiles = this.isInViewport('load_more_button')
        if (loadNewFiles) {
          this.fetchingNext = true
          TemporaryFileService.loadNextPage(this.nextUrl)
            .then((response) => {
              this.nextUrl = response.data.next
              this.temporaryFiles = [...this.temporaryFiles, ...response.data.results]
              this.tableKey++
            })
            .finally(() => {
              this.fetchingNext = false
            })
        }
      }
    }
  },
}
</script>

<style lang="scss">
.files-table-row {
  height: 52px;

  .no-avatar {
    font-size: 20px;
    color: #666;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ddd;
    border-radius: 20px;
    padding-top: 2px;
  }

  td {
    padding: 20px;
  }

  .col-file-filename {
    width: 70%;
  }

  .col-file-status {
    width: 10%;
  }

  .col-file-created {
    width: 10%;
    white-space: nowrap;
  }
}

.files-table-row-hover {
  cursor: pointer !important;

  &:hover {
    background-color: #ebf5fa !important;
    z-index: 2;
    box-shadow: inset 0 0 0 #dadce0, inset 0 0 0 #dadce0, 0 1px 2px 0 rgba(60, 64, 67, 0.3),
      0 1px 5px 1px rgba(60, 64, 67, 0.15);
  }
}

.gk-table.files-table tbody tr {
  cursor: default;
}
</style>

<style scoped lang="scss">
.files-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 1rem;
}

.files-row-name {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
}

.active {
  color: #2eb090;

  &:hover {
    text-decoration: underline;
  }
}

.expired {
  color: #7b7b7b;
  text-decoration: line-through;
}

.processing {
  color: #7b7b7b;
}
</style>
